/**
 * The form that adds a period: a label and the date the period ends.
 *
 * Two places host it — the period switcher's menu, and the board's empty state
 * when a workflow has no period yet, where it is the first thing to do and so
 * sits on the page rather than behind a menu.
 */

import React, { useState } from "react";

/**
 * What `onCreate` answers with. It reports failure rather than throwing: a
 * server that refuses a duplicate period end is answering the form, not
 * crashing it, and a rejected promise here reached the page as an unhandled
 * `MonitoringConflictError` behind a red error overlay.
 */
export interface PeriodCreateResult {
	ok: boolean;
	/** A 409 — the date collides with a period that already exists. */
	conflict?: boolean;
	message?: string;
}

export type PeriodCreateHandler = (payload: {
	label: string;
	period_end: string;
}) => Promise<PeriodCreateResult>;

interface NewPeriodFormProps {
	/** Must not reject; a failure comes back as `{ ok: false }`. */
	onCreate: PeriodCreateHandler;
	/** Called after a period is added. */
	onDone?: () => void;
	/** Shows a Cancel button when given. */
	onCancel?: () => void;
	/** `row` lays the fields out side by side, for the empty state. */
	layout?: "stack" | "row";
}

const INPUT =
	"h-9 rounded-lg border px-2.5 text-sm font-normal text-[#111111] focus:outline-none focus:ring-1";

const NewPeriodForm: React.FC<NewPeriodFormProps> = ({
	onCreate,
	onDone,
	onCancel,
	layout = "stack",
}) => {
	const [label, setLabel] = useState("");
	const [periodEnd, setPeriodEnd] = useState("");
	const [saving, setSaving] = useState(false);
	// The conflict the server named, under the field it is about. A 409 here is
	// the user's own input, not a transient failure, so it stays on screen
	// until they change the date — a toast alone would scroll away from it.
	const [conflict, setConflict] = useState<string | null>(null);

	const submit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (saving) return;
		if (!label.trim() || !periodEnd) return;
		setSaving(true);
		setConflict(null);
		try {
			// The caller's contract says this never rejects; the catch is the
			// belt to that braces, so a contract violation still cannot put an
			// unhandled rejection on the page.
			const result = await onCreate({
				label: label.trim(),
				period_end: periodEnd,
			}).catch(() => ({ ok: false }) as PeriodCreateResult);
			if (!result.ok) {
				// Kept open with what was typed: the write did not happen, and
				// clearing the form would make the user retype it.
				if (result.conflict) {
					setConflict(result.message ?? "That period already exists.");
				}
				return;
			}
			setLabel("");
			setPeriodEnd("");
			onDone?.();
		} finally {
			setSaving(false);
		}
	};

	const row = layout === "row";

	return (
		<form
			onSubmit={submit}
			aria-label="New period"
			className={row ? "flex flex-wrap items-start gap-3" : "flex flex-col gap-2.5"}
		>
			<label
				className={`flex flex-col gap-1 text-xs font-semibold text-[#3f4247] ${row ? "w-44" : ""}`}
			>
				Label
				<input
					type="text"
					value={label}
					onChange={(event) => setLabel(event.target.value)}
					placeholder="Q1 2026"
					required
					className={`${INPUT} border-gray-300 focus:border-[#004C4B] focus:ring-[#004C4B]`}
				/>
			</label>
			{/* The error sits OUTSIDE the label: a wrapping label lends its
			    whole text to the field's accessible name. */}
			<div className={`flex flex-col gap-1 ${row ? "w-44" : ""}`}>
				<label className="flex flex-col gap-1 text-xs font-semibold text-[#3f4247]">
					Period end
					<input
						type="date"
						value={periodEnd}
						onChange={(event) => {
							setPeriodEnd(event.target.value);
							// The complaint was about the old date.
							setConflict(null);
						}}
						required
						aria-invalid={conflict !== null}
						className={`${INPUT} ${
							conflict
								? "border-red-400 focus:border-red-500 focus:ring-red-500"
								: "border-gray-300 focus:border-[#004C4B] focus:ring-[#004C4B]"
						}`}
					/>
				</label>
				{conflict && (
					<span role="alert" className="text-xs text-red-700">
						{conflict}
					</span>
				)}
			</div>
			<div className={`flex gap-2 ${row ? "pt-[22px]" : "justify-end"}`}>
				{onCancel && (
					<button
						type="button"
						onClick={() => {
							setConflict(null);
							onCancel();
						}}
						className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-[#1F2937] hover:bg-gray-50"
					>
						Cancel
					</button>
				)}
				<button
					type="submit"
					disabled={saving}
					className="h-9 rounded-lg bg-[#004C4B] px-3.5 text-sm font-semibold text-white hover:bg-[#003a3a] disabled:opacity-60"
				>
					{saving ? "Adding…" : "Add period"}
				</button>
			</div>
		</form>
	);
};

export default NewPeriodForm;
